import { Colors, Services, getServiceNameViaPoint } from '@sigma-frontend-libs/commons'

import { getLocaleDateString, getLocaleTimeString, icons } from '@/utils'

import { reportManagerQueueStatuses } from '@/components/services/report-manager/queue/utils'
import { reportManagerRecurrentStatuses } from '@/components/services/report-manager/recurrent/utils'

const timelineServiceConfig = {
  [Services['report-manager/queue']]: {
    fields: [
      'createdAt',
      'scheduledTime',
      'dispatchedAt',
      'actualRunTime'
    ],
    statuses: reportManagerQueueStatuses
  },
  [Services['report-manager/recurrent']]: {
    fields: [
      'createdAt',
      'nextStartDate'
    ],
    statuses: reportManagerRecurrentStatuses
  }
}

const formatTimelineDate = date => {
  if (!date) {
    return null
  }

  return [
    getLocaleDateString(date),
    getLocaleTimeString(date, { second: '2-digit' })
  ].filter(Boolean).join(' ').trim()
}

/**
 * Преобразует набор событий во временную шкалу с относительными позициями точек.
 *
 * @param {Array<{ timestamp: number | null, isPast: boolean, [key: string]: unknown }>} events
 * @returns {Array<{ timestamp: number, position: number, isPast: boolean, event: Record<string, unknown> }>}
 */
const createTimelinePoints = events => {
  const datedEvents = events.filter(event => {
    return typeof event.timestamp === 'number'
  })

  if (!datedEvents.length) {
    return []
  }

  const timestamps = datedEvents.map(event => {
    return event.timestamp
  })
  const minTimestamp = Math.min(...timestamps)
  const maxTimestamp = Math.max(...timestamps)
  const duration = Math.max(maxTimestamp - minTimestamp, 1)

  return datedEvents.map(event => {
    const relativePosition = (event.timestamp - minTimestamp) / duration
    let position = 0
    if (Number.isFinite(relativePosition)) {
      position = relativePosition
    }

    return {
      timestamp: event.timestamp,
      position,
      isPast: event.isPast,
      event
    }
  })
}

/**
 * Нормализует исходное значение в единый объект события для временной шкалы.
 *
 * @param {{
 *   nowTimestamp: number,
 *   label?: string | null,
 *   value?: string | number | Date | null,
 *   dateOverride?: string | null,
 *   isError?: boolean,
 *   color?: string,
 *   icon?: string
 * }} options
 * @returns {{ label: string | null, date: string | null, timestamp: number | null, isPast: boolean, isError: boolean, color?: string, icon?: string }}
 */
const createTimelineEvent = ({ nowTimestamp,
  label,
  value,
  dateOverride,
  isError = false,
  color,
  icon }) => {
  let dateValue = null
  if (value) {
    dateValue = new Date(value)
  }

  let timestamp = null
  if (dateValue) {
    timestamp = dateValue.getTime()
  }

  let resolvedLabel = null
  if (label !== null && typeof label !== 'undefined') {
    resolvedLabel = label
  }

  let resolvedDate = formatTimelineDate(dateValue)
  if (dateOverride !== null && typeof dateOverride !== 'undefined') {
    resolvedDate = dateOverride
  }

  return {
    label: resolvedLabel,
    date: resolvedDate,
    timestamp,
    isPast: timestamp !== null && timestamp <= nowTimestamp,
    isError,
    color,
    icon
  }
}

const extractDebugPayloadErrorMessage = payload => {
  if (typeof payload !== 'object' || payload === null) {
    return ''
  }

  const payloadWithError = payload
  const error = payloadWithError.error
  if (typeof error !== 'object' || error === null) {
    return ''
  }

  const message = error.message

  if (typeof message === 'string') {
    return message
  }

  return ''
}

/**
 * Склеивает обычное сообщение шага и ошибку из debugPayload в одну подпись.
 *
 * @param {{ message?: string, payload?: unknown } | undefined} item
 * @returns {string}
 */
export const createDebugPayloadErrorMessage = item => {
  let baseMessage = ''
  if (typeof item?.message === 'string') {
    baseMessage = item.message
  }

  const errorMessage = extractDebugPayloadErrorMessage(item?.payload)

  if (baseMessage && errorMessage) {
    return `${baseMessage}<br>${errorMessage}`
  }
  if (baseMessage) {
    return baseMessage
  }

  return errorMessage
}

/**
 * Преобразует debugPayload в дополнительные события шкалы.
 *
 * @param {{ debugPayload?: Array<{ createdAt?: string | null, message?: string, payload?: unknown }> } | undefined} value
 * @param {number} nowTimestamp
 * @returns {Array<ReturnType<typeof createTimelineEvent>>}
 */
const createDebugTimelineEvents = (value, nowTimestamp) => {
  const payload = value?.debugPayload
  if (!Array.isArray(payload) || payload.length === 0) {
    return []
  }

  return payload
    .map(item => {
      let createdAt = null
      if (item?.createdAt) {
        createdAt = item.createdAt
      }

      return createTimelineEvent({
        nowTimestamp,
        label: createDebugPayloadErrorMessage(item),
        value: createdAt,
        isError: true
      })
    })
}

/**
 * Сортирует события так, чтобы пустые даты всегда уходили в конец.
 *
 * @param {Array<ReturnType<typeof createTimelineEvent>>} events
 * @returns {Array<ReturnType<typeof createTimelineEvent>>}
 */
const sortTimelineEvents = events => {
  if (!events.length) {
    return []
  }

  return [ ...events ].sort((first, second) => {
    if (first.timestamp === null && second.timestamp === null) {
      return 0
    }
    if (first.timestamp === null) {
      return 1
    }
    if (second.timestamp === null) {
      return -1
    }

    return first.timestamp - second.timestamp
  })
}

const getStatusMetaByService = (service, status) => {
  if (!status) {
    return undefined
  }

  return timelineServiceConfig[service]?.statuses?.[status]
}

/**
 * Создаёт текущее статусное событие, которое всегда должно оставаться активной точкой.
 *
 * @param {string} service
 * @param {string} status
 * @param {string} statusMetaLabel
 * @param {number} nowTimestamp
 * @param {number | null} timestampOverride
 * @returns {ReturnType<typeof createTimelineEvent> & { timestamp: number, isPast: false }}
 */
const createCurrentTimelineEvent = (service, status, statusMetaLabel, nowTimestamp, timestampOverride) => {
  const statusMeta = getStatusMetaByService(service, status)
  let resolvedTimestamp = nowTimestamp
  if (typeof timestampOverride === 'number') {
    resolvedTimestamp = timestampOverride
  }

  const baseEvent = createTimelineEvent({
    nowTimestamp,
    label: statusMetaLabel,
    value: resolvedTimestamp,
    color: statusMeta?.color,
    icon: statusMeta?.icon
  })

  return {
    ...baseEvent,
    timestamp: resolvedTimestamp,
    isPast: false
  }
}

export const getPointColor = point => {
  if (point.event.color) {
    return point.event.color
  }
  if (point.event.isError) {
    return Colors.error
  }

  if (point.isPast) {
    return Colors.success
  }

  return Colors.grey
}
export const getPointIcon = point => {
  if (point.event.icon) {
    return point.event.icon
  }
  if (point.event.isError) {
    return icons.clear
  }

  if (point.isPast) {
    return icons.done
  }

  return icons.more_horiz
}

/**
 * Строит итоговую временную шкалу по данным задания и текущему статусу.
 *
 * @param {Record<string, unknown>} value
 * @param {string} service
 * @returns {Array<{ timestamp: number, position: number, isPast: boolean, event: Record<string, unknown> }>}
 */
export function getTimelinePoints(value, service) {
  const serviceNameViaPoint = getServiceNameViaPoint(service)
  const nowTimestamp = Date.now()
  const currentStatus = value.status
  let currentStatusLabel = ''
  if (currentStatus) {
    currentStatusLabel = this.getTranslate(`${Services['report-manager']}.statuses.${currentStatus}`)
  }

  const timelineFields = timelineServiceConfig[service]?.fields ?? []
  const baseEvents = timelineFields.map(field => {
    const rawValue = value?.[field]
    let dateInput = null
    if (rawValue instanceof Date || typeof rawValue === 'string' || typeof rawValue === 'number') {
      dateInput = rawValue
    }

    return createTimelineEvent({
      nowTimestamp,
      label: this.getTranslate(`${serviceNameViaPoint}.labels.${field}`),
      value: dateInput
    })
  })

  const debugEvents = createDebugTimelineEvents(value, nowTimestamp)
  const mergedEvents = [ ...baseEvents, ...debugEvents ]

  if (currentStatus && currentStatusLabel) {
    let currentStatusTimestamp = null
    if (value.updatedAt) {
      currentStatusTimestamp = new Date(value.updatedAt).getTime()
    }

    const currentEvent = createCurrentTimelineEvent(
      service,
      currentStatus,
      currentStatusLabel,
      nowTimestamp,
      currentStatusTimestamp
    )

    mergedEvents.push(currentEvent)
  }

  const events = sortTimelineEvents(mergedEvents)

  return createTimelinePoints(events)
}
